import type { Me } from '@shared/contracts/identity'
import { hasPermission, type Permission } from '@shared/permissions'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react'
import { http } from './api'

interface AuthContextValue {
  me: Me | undefined
  user: Me['user'] | null
  isLoading: boolean
  can: (permission: Permission) => boolean
  hasScopedRole: (roleCode: string, scopeType?: string, scopeId?: string) => boolean
  signOut: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const ME_QUERY_KEY = ['me'] as const

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient()
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => http.get<Me>('/api/v1/me'),
    staleTime: 60_000,
    retry: false,
  })
  const user = query.data?.user ?? null
  const can = useCallback(
    (permission: Permission) => (user ? hasPermission(user.permissions, permission) : false),
    [user],
  )
  const hasScopedRole = useCallback(
    (roleCode: string, scopeType?: string, scopeId?: string) =>
      !!user?.roles.some(
        (r) =>
          r.code === roleCode &&
          (scopeType === undefined || r.scopeType === scopeType) &&
          (scopeId === undefined || r.scopeId === scopeId),
      ),
    [user],
  )
  const signOut = useCallback(async () => {
    await http.post('/auth/sign-out')
    await qc.invalidateQueries()
    window.location.href = '/'
  }, [qc])
  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ME_QUERY_KEY })
  }, [qc])
  const value = useMemo<AuthContextValue>(
    () => ({
      me: query.data,
      user,
      isLoading: query.isLoading,
      can,
      hasScopedRole,
      signOut,
      refresh,
    }),
    [query.data, user, query.isLoading, can, hasScopedRole, signOut, refresh],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const v = useContext(AuthContext)
  if (!v) throw new Error('useAuth must be used inside AuthProvider')
  return v
}

export function signInUrl(redirectTo?: string) {
  const target = redirectTo ?? window.location.pathname + window.location.search
  return `/sign-in?redirectTo=${encodeURIComponent(target)}`
}
